import React from "react";
import { useSelector } from "react-redux";

import { IconEnlarge, IconPinCrossed } from "~/components/icon";
import { Truncated } from "~/components/truncated";
import { ZIndexBoundary } from "~/components/z-index-boundary";
import { cx } from "~/tools/class-variance";
import type { Time } from "~/tools/date-time/time";
import type { Impulse } from "~/tools/impulse";
import { prop } from "~/tools/prop";

import { selectTimeRange, selectTimestampDate } from "../../../../../../app/state/view/timeline/TimelineSelectors";
import { Widget, WidgetBody, WidgetHeader } from "../../../../../../components/_widget";
import { CtxMenuItem } from "../../../../../../components/react/CtxMenu/CtxMenuItem";
import { CtxPopoverMenu } from "../../../../../../components/react/CtxMenu/CtxPopoverMenu";
import { ComponentLinkWithType } from "../../../../../../components/react/component-link/ComponentLinkWithType";
import { Tooltip } from "../../../../../../components/react/tooltip/Tooltip";
import { queryUIConfig } from "../../../../../../queries";
import { useQuery } from "../../../../../../queries/react-query";

export interface ComponentReference {
	identifier: string;
	type: string;
	name: string;
	timestamp?: Time;
}

export interface PinnedItemWidgetProps {
	isExpanded: Impulse<boolean>;
	name: string;
	title: undefined | string;
	description: undefined | string;
	component: undefined | ComponentReference;
	onUnpin: VoidFunction;
	onAddToDashboard: false | VoidFunction;
	onEnlarge: false | VoidFunction;
}

export const PinnedItemWidget: React.FC<React.PropsWithChildren<PinnedItemWidgetProps>> = ({
	isExpanded,
	name,
	title,
	description,
	onUnpin,
	onAddToDashboard,
	onEnlarge,
	component,
	children,
}) => {
	const timeRange = useSelector(selectTimeRange);
	const timestamp = useSelector(selectTimestampDate);
	const { data: webUIConfig } = useQuery({
		...queryUIConfig,
		select: prop("webUIConfig"),
	});

	return (
		<Widget
			card
			data-testid="pinned-item"
			divided
			isExpanded={isExpanded}
			namespace={["pinned-card", "pinned-items"]}
			py="sm"
		>
			<WidgetHeader
				className="sticky top-14 z-10 bg-white"
				description={
					component && (
						<ComponentLinkWithType
							className="text-13/[21px] text-gray-600"
							data-testid="card-subtitle"
							identifier={component.identifier}
							name={component.name}
							prefix="on"
							timeRange={timeRange}
							timestamp={component.timestamp ?? timestamp}
							type={component.type}
						/>
					)
				}
				size="sm"
				title={
					title && (
						<Truncated
							className="font-medium text-gray-800"
							tooltip={description ? `${title} - ${description}` : undefined}
						>
							{title}
						</Truncated>
					)
				}
			>
				<div className="flex gap-4" onClick={(event) => event.stopPropagation()}>
					<Tooltip title="Remove from pinned items">
						<IconButton data-testid="remove-icon" onClick={onUnpin} role="button">
							<IconPinCrossed className="text-15" />
						</IconButton>
					</Tooltip>
					{onEnlarge && (
						<Tooltip title={`Show the detailed ${name}`}>
							<IconButton data-testid="enlarge-icon" onClick={onEnlarge} role="button">
								<IconEnlarge />
							</IconButton>
						</Tooltip>
					)}

					{webUIConfig?.featureFlags.dashboards && onAddToDashboard && (
						<ZIndexBoundary zIndex={(closestZIndex = 0) => closestZIndex + 50}>
							{(zIndex) => (
								<CtxPopoverMenu ellipsisClassName="m-0" zIndex={zIndex}>
									<CtxMenuItem label="Add to dashboard" onClick={onAddToDashboard} />
								</CtxPopoverMenu>
							)}
						</ZIndexBoundary>
					)}
				</div>
			</WidgetHeader>

			<WidgetBody>{children}</WidgetBody>
		</Widget>
	);
};

const IconButton: React.FC<React.ComponentProps<"button">> = ({ className, ...props }) => (
	<button
		{...props}
		className={cx(
			"cursor-pointer rounded-full border-none bg-transparent p-0 text-13 text-gray-800 outline-hidden hover:text-primary-500 focus:text-primary-500 active:text-primary-500",
			className,
		)}
	/>
);
